import { useEffect } from "react";
// @ts-ignore — react-dom is a peer dependency
import { createPortal } from "react-dom";
import type { TranslateFn } from "../../i18n";
import type { Profile } from "../types";
import { XIcon } from "../icons";
import { ProfileEditView } from "./ProfileEditView";

/**
 * Edit profile dialog — opens as a dark overlay ABOVE the main modal
 * (z-index matches the .ps-tryon-overlay max-int so it always stacks
 * on top). Wraps ProfileEditView so the user gets a real editable form
 * with name + gender + units + height/weight/age + Save / Cancel.
 *
 * Portaled to document.body so parent stacking contexts can't clip it.
 */

export function ProfileDetailModal({
  profileDetail, setProfileDetail, setProfiles, activeProfileId, setActiveProfileId, t, portalContainer,
}: {
  profileDetail: Profile | null;
  setProfileDetail: (p: Profile | null) => void;
  setProfiles: React.Dispatch<React.SetStateAction<Profile[]>>;
  activeProfileId: string | null;
  setActiveProfileId: (id: string | null) => void;
  t: TranslateFn;
  portalContainer?: Element | DocumentFragment;
}) {
  // Lock scroll while open
  useEffect(() => {
    if (!profileDetail || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [profileDetail]);

  if (!profileDetail) return null;
  if (typeof document === "undefined") return null;

  const handleSave = (updated: Omit<Profile, "id" | "createdAt">) => {
    setProfiles((prev) => prev.map((p) =>
      p.id === profileDetail.id
        ? {
            ...p,
            ...updated,
            // Keep id + createdAt + sizeHistory + measurements; bump lastEditedAt
            id: p.id,
            createdAt: p.createdAt,
            sizeHistory: p.sizeHistory,
            measurements: p.measurements,
            lastEditedAt: Date.now(),
          }
        : p
    ));
    setProfileDetail(null);
  };

  const handleClose = () => setProfileDetail(null);

  // Suppress unused param warnings — kept for backward compat with prior consumers
  void activeProfileId;
  void setActiveProfileId;

  return createPortal(
    <div
      className="ps-tryon-detail-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="ps-tryon-detail-modal">
        <div className="ps-tryon-detail-header">
          <span>{t("Edit Profile")}</span>
          <button onClick={handleClose} aria-label={t("Close")}>
            <XIcon size={18} />
          </button>
        </div>
        <div className="ps-tryon-detail-body">
          <ProfileEditView
            profile={profileDetail}
            onSave={handleSave}
            onCancel={handleClose}
            t={t}
          />
        </div>
      </div>
    </div>,
    portalContainer ?? document.body
  );
}
